import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ShoppingBagIcon, PlusCircleIcon, CupSodaIcon, PlusIcon } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { CartItem } from './CartItem';
import { CartItemType, Product } from '../types/types';
import { useLanguage } from '../context/LanguageContext';
import { MENU_PRODUCTS } from '../data/menuData';
interface CartProps {
  onCheckout: () => void;
  onEditItem: (item: CartItemType) => void;
}
export function Cart({
  onCheckout,
  onEditItem
}: CartProps) {
  const {
    isCartOpen,
    toggleCart,
    items,
    cartTotal,
    updateQuantity,
    removeItem,
    addItem
  } = useCart();
  const {
    t,
    formatPrice,
    translateSubcategory
  } = useLanguage();
  const totalFormatted = formatPrice(cartTotal);
  const [showBeverages, setShowBeverages] = useState(false);
  const [activeSubcategory, setActiveSubcategory] = useState<string | null>(null);
  const beverageGroups = useMemo(() => {
    const groups: Record<string, Product[]> = {};
    MENU_PRODUCTS.filter(p => p.category === 'Bebidas').forEach(p => {
      const sub = p.subcategory || 'Otras';
      if (!groups[sub]) groups[sub] = [];
      groups[sub].push(p);
    });
    return groups;
  }, []);
  // Set initial subcategory when opening beverages
  useEffect(() => {
    if (showBeverages && !activeSubcategory) {
      setActiveSubcategory(Object.keys(beverageGroups)[0]);
    }
  }, [showBeverages, activeSubcategory, beverageGroups]);
  const handleAddBeverage = (product: Product) => {
    addItem({
      id: `${product.id}_${Date.now()}`,
      productId: product.id,
      name: product.name,
      basePrice: product.price,
      quantity: 1,
      selections: {},
      subtotal: product.price,
      type: 'bebida'
    });
  };
  return <AnimatePresence data-id="element-677">
      {isCartOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={toggleCart} className="fixed inset-0 bg-black/40 z-50 backdrop-blur-sm max-w-md mx-auto" data-id="element-678" />
          <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className="fixed bottom-0 left-0 right-0 z-50 bg-carbon rounded-t-3xl max-h-[85vh] flex flex-col max-w-md mx-auto shadow-2xl border-t border-gray-800" data-id="element-679">
            <div className="bg-surface px-6 py-4 rounded-t-3xl flex items-center justify-between border-b border-gray-800" data-id="element-680">
              <h2 className="font-heading font-bold text-xl text-white flex items-center gap-2 tracking-wide" data-id="element-681">
                <ShoppingBagIcon className="w-5 h-5 text-verde" data-id="element-682" />
                {t('yourOrder')}
              </h2>
              <button onClick={toggleCart} className="p-2 bg-surface-light rounded-full text-gray-400 hover:text-white" data-id="element-683">
                <XIcon className="w-5 h-5" data-id="element-684" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4" data-id="element-685">
              {items.length === 0 ? <div className="flex flex-col items-center justify-center h-48 text-gray-400" data-id="element-686">
                  <ShoppingBagIcon className="w-12 h-12 mb-3 opacity-50" data-id="element-687" />
                  <p data-id="element-688">{t('emptyCart')}</p>
                </div> : <div className="space-y-1" data-id="element-689">
                  {items.map(item => <CartItem key={item.id} item={item} onUpdateQuantity={updateQuantity} onRemove={removeItem} onEdit={i => {
              toggleCart();
              onEditItem(i);
            }} data-id="element-690" />)}
                </div>}

              {/* Action Buttons */}
              {items.length > 0 && <div className="mt-6 flex gap-3" data-id="element-691">
                  <button onClick={toggleCart} className="flex-1 bg-surface-light border border-gray-700 text-white py-3 rounded-xl flex items-center justify-center gap-2 text-sm font-medium hover:bg-gray-800 transition-colors active:scale-95" data-id="element-692">
                    <PlusCircleIcon className="w-4 h-4 text-gray-400" data-id="element-693" />
                    {t('addSomethingMore')}
                  </button>
                  <button onClick={() => setShowBeverages(!showBeverages)} className={`flex-1 border py-3 rounded-xl flex items-center justify-center gap-2 text-sm font-medium transition-colors active:scale-95 ${showBeverages ? 'bg-verde/10 border-verde text-verde' : 'bg-surface-light border-gray-700 text-white hover:bg-gray-800'}`} data-id="element-694">
                    <CupSodaIcon className={`w-4 h-4 ${showBeverages ? 'text-verde' : 'text-gray-400'}`} data-id="element-695" />
                    {t('addBeverage')}
                  </button>
                </div>}

              {/* Inline Beverage Picker */}
              <AnimatePresence data-id="element-696">
                {showBeverages && items.length > 0 && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} className="mt-4 overflow-hidden" data-id="element-697">
                    <div className="bg-surface-light border border-gray-800 rounded-2xl p-4" data-id="element-698">
                      {/* Subcategory Tabs */}
                      <div className="flex overflow-x-auto hide-scrollbar gap-2 mb-4 pb-1" data-id="element-699">
                        {Object.keys(beverageGroups).map(sub => <button key={sub} onClick={() => setActiveSubcategory(sub)} className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeSubcategory === sub ? 'bg-verde text-carbon' : 'bg-surface text-gray-400 border border-gray-700 hover:text-white'}`} data-id="element-700">
                            {translateSubcategory(sub)}
                          </button>)}
                      </div>

                      {/* Beverage List */}
                      <div className="space-y-3" data-id="element-701">
                        {activeSubcategory && beverageGroups[activeSubcategory]?.map(beverage => <div key={beverage.id} className="flex items-center justify-between p-3 bg-surface rounded-xl border border-gray-800" data-id="element-702">
                              <div className="flex items-center gap-3" data-id="element-703">
                                {beverage.image && <img src={beverage.image} alt={beverage.name} className="w-12 h-12 rounded-lg object-cover" data-id="element-704" />}
                                <div data-id="element-705">
                                  <h4 className="text-white text-sm font-medium" data-id="element-706">
                                    {beverage.name}
                                  </h4>
                                  <p className="text-verde text-sm font-semibold" data-id="element-707">
                                    {formatPrice(beverage.price).primary}
                                  </p>
                                </div>
                              </div>
                              <button onClick={() => handleAddBeverage(beverage)} className="p-2 bg-verde/10 text-verde rounded-full hover:bg-verde/20 transition-colors active:scale-90" data-id="element-708">
                                <PlusIcon className="w-5 h-5" data-id="element-709" />
                              </button>
                            </div>)}
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {items.length > 0 && <div className="bg-surface p-6 border-t border-gray-800 pb-safe" data-id="element-710">
                <div className="flex justify-between items-center mb-4" data-id="element-711">
                  <span className="text-gray-400 font-medium" data-id="element-712">
                    {t('subtotal')}
                  </span>
                  <div className="flex flex-col items-end" data-id="element-713">
                    <span className="font-bold text-lg text-white" data-id="element-714">
                      {totalFormatted.primary}
                    </span>
                    <span className="text-sm text-gray-400" data-id="element-715">
                      {totalFormatted.secondary}
                    </span>
                  </div>
                </div>
                <button onClick={() => {
            toggleCart();
            onCheckout();
          }} className={`w-full font-semibold py-4 rounded-xl shadow-lg transition-colors active:scale-95 border border-white/10 ${new URLSearchParams(window.location.search).get('table') ? 'bg-dorado text-carbon shadow-dorado/20 hover:bg-dorado/90' : 'bg-terracota text-white shadow-terracota/20 hover:bg-[#9a3a18]'}`} data-id="element-716">
                  {new URLSearchParams(window.location.search).get('table') ? '🍽️ Enviar a cocina' : t('continueCheckout')}
                </button>
              </div>}
          </motion.div>
        </>}
    </AnimatePresence>;
}